import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import App from '../App';
import LandingPage from './LandingPage';
import LoginPage from './LoginPage';
import SignupPage from './SignupPage';
import OnboardingFlow from './OnboardingFlow';
import AuthCallback from './AuthCallback';
import EmergencyReset from './EmergencyReset';
import AdminPanel from './AdminPanel';
import ResearchMockDemo from './research/mocks/ResearchMockDemo';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';
// Import AuthProvider and useAuth hook
import { AuthProvider, useAuth } from '../context/AuthContext';
// Import Intercom hook
import { useIntercom } from '../hooks/useIntercom';

/**
 * Protected Route Component
 * Ensures routes are only accessible when authenticated
 * Uses the AuthContext to manage authentication state across the app
 *
 * IMPORTANT: Component stays mounted during background auth operations (like token refresh)
 * to preserve component state. Loading overlay is shown on top instead of unmounting.
 */
const ProtectedRoute = ({ element }) => {
  // Use the auth context instead of local state
  const { loading, isAuthenticated } = useAuth();

  // If not authenticated, show loading or redirect to login
  if (!isAuthenticated) {
    return loading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    ) : (
      <Navigate to="/login" />
    );
  }

  // If authenticated, show element and optionally show loading overlay
  // This prevents unmounting the component during background auth operations
  return (
    <>
      {element}
      {loading && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(0, 0, 0, 0.3)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <CircularProgress />
        </Box>
      )}
    </>
  );
};

/**
 * Public or App Route Component
 * Shows LandingPage for unauthenticated users, App for authenticated users
 * This is used for the root "/" route
 */
const PublicOrAppRoute = () => {
  const { loading, isAuthenticated } = useAuth();

  // Show loading indicator while auth state is being checked
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show LandingPage if not authenticated, App if authenticated
  return isAuthenticated ? <App /> : <LandingPage />;
};

/**
 * Intercom Initializer Component
 * Must be inside AuthProvider to access auth context
 */
const IntercomInitializer = () => {
  useIntercom();
  return null;
};

/**
 * App Router Component
 * Manages application routing and authentication flows
 * Wraps the entire application with the AuthProvider
 */
const AppRouter = () => {
  // Layout component that provides the auth context
  const AuthLayout = () => {
    return (
      <AuthProvider>
        <IntercomInitializer />
        <Outlet />
      </AuthProvider>
    );
  };

  // Define routes with the createBrowserRouter function
  const router = createBrowserRouter([
    {
      element: <AuthLayout />,
      children: [
        {
          path: "/",
          element: <PublicOrAppRoute />
        },
        {
          path: "/tickets",
          element: <ProtectedRoute element={<App />} />
        },
        {
          path: "/admin",
          element: <ProtectedRoute element={<AdminPanel />} />
        },
        {
          path: "/login",
          element: <LoginPage />
        },
        {
          path: "/signup",
          element: <SignupPage />
        },
        {
          path: "/terms",
          element: <TermsOfService />
        },
        {
          path: "/privacy",
          element: <PrivacyPolicy />
        },
        {
          path: "/onboarding",
          element: <ProtectedRoute element={<OnboardingFlow />} />
        },
        {
          path: "/auth/callback",
          element: <AuthCallback />
        },
        {
          path: "/emergency-reset",
          element: <EmergencyReset />
        },
        {
          path: "/mock-demo",
          element: <ResearchMockDemo />
        },
        {
          path: "*",
          element: <Navigate to="/" replace />
        }
      ]
    }
  ]);

  return <RouterProvider router={router} />;
};

export default AppRouter;
